import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, formatIdr } from '../api/client';
import { Alert, Button, Card, Spinner } from '../components/ui';
import { useT } from '../i18n/locale';

interface Invoice {
  number: string;
  plan_code: string;
  amount_idr: number;
  tax_idr: number;
  total_idr: number;
  status: string;
}

/**
 * Stand-in for a payment gateway's hosted checkout.
 *
 * Reachable only while BILLING_PROVIDER=mock outside production; the server
 * refuses the pay call otherwise, and startup refuses the provider itself in
 * production. Styled as an obvious test harness so it can never be mistaken
 * for a real payment page.
 */
export default function MockPayScreen() {
  const t = useT();
  const { number } = useParams<{ number: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isMock, setIsMock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ invoice: Invoice; isMock: boolean }>(`/billing/invoices/${number}`)
      .then((d) => {
        setInvoice(d.invoice);
        setIsMock(d.isMock);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : t('mock.notFound')))
      .finally(() => setLoading(false));
  }, [number]);

  async function pay() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/billing/mock/${number}/pay`);
      navigate('/billing?status=success');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('mock.markFailed'));
      setBusy(false);
    }
  }

  if (loading) return <Spinner label={t('mock.loading')} />;

  return (
    <div className="mx-auto w-full max-w-lg p-6">
      <div className="mb-3 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 px-4 py-2.5 text-center text-xs font-bold tracking-wide text-amber-900 uppercase">
        {t('mock.banner')}
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="text-xs font-bold tracking-wider text-slate-500 uppercase">
            {t('mock.invoice')}
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900">{invoice?.number}</div>
        </div>

        <div className="space-y-3 px-6 py-5">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <dl className="m-0 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('mock.plan')}</dt>
              <dd className="m-0 font-bold text-slate-800 capitalize">{invoice?.plan_code}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('mock.price')}</dt>
              <dd className="m-0 font-semibold text-slate-800">
                {formatIdr(invoice?.amount_idr ?? 0)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('mock.vat')}</dt>
              <dd className="m-0 font-semibold text-slate-800">
                {formatIdr(invoice?.tax_idr ?? 0)}
              </dd>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-2">
              <dt className="font-bold text-slate-800">{t('mock.total')}</dt>
              <dd className="m-0 text-lg font-black text-slate-900">
                {formatIdr(invoice?.total_idr ?? 0)}
              </dd>
            </div>
          </dl>

          {invoice?.status === 'paid' ? (
            <Alert tone="success">{t('mock.alreadyPaid')}</Alert>
          ) : !isMock ? (
            <Alert tone="warn">{t('mock.disabled')}</Alert>
          ) : null}
        </div>

        <div className="flex gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <Button variant="secondary" onClick={() => navigate('/billing')}>
            Kembali
          </Button>
          <div className="flex-1" />
          <Button
            onClick={() => void pay()}
            disabled={busy || !isMock || invoice?.status === 'paid'}
          >
            {busy ? t('mock.processing') : t('mock.markPaid')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
